import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Page as Page$1, usePageContext } from 'react-pdf';
import PagePlaceholder from './PagePlaceholder.js';
import { usePDFContent } from './PDF.js';
import { styled, css } from 'styled-components';
import { useDebounce } from '@fabric-app/hooks/useDebounce';
import { usePDFViewControls } from './PDFViewControls.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import { cssVar } from '@fabric-app/ui/theme/variables';

const Wrapper = styled.div.withConfig({
  displayName: "RowPage__Wrapper",
  componentId: "sc-3b8ebc76-0"
})([
  `position:relative;z-index:2;height:100%;width:100%;`
]);
const Page = styled(Page$1).withConfig({
  displayName: "RowPage__Page",
  componentId: "sc-3b8ebc76-1"
})([
  `background:none !important;&[data-resizing='true']{display:none;}z-index:2;span[data-search],mark,span[data-annotation]{position:static;color:inherit;padding:0;margin:0;border-radius:2px;display:inline;line-height:inherit;font-size:inherit;font-weight:inherit;font-family:inherit;font-style:inherit;text-align:inherit;white-space:inherit;box-decoration-break:inherit;-webkit-box-decoration-break:inherit;box-shadow:none;background:none;}span[data-search]{background-color:rgba(255,252,158,0.45);}mark{opacity:0.35;position:relative;background-color:#fffb9e;box-shadow:0 0 0 2px #fffb9e,inset 0 0 0 1000px #fffb9e;}span[data-annotation]{opacity:1;background-color:`,
  `;box-shadow:none;&:hover{background-color:`,
  `;box-shadow:none;}}`,
  ` canvas,[data-canvas-placeholder]{width:100% !important;height:100% !important;pointer-events:none;}.textLayer{display:flex;user-select:auto;*{user-select:auto;}}`
], cssVar["color-bg-annotation"], cssVar["color-bg-annotation-hover"], ({ activeMatchId }) => {
  if (!activeMatchId) return "";
  return css([
    `span[data-search-id*='`,
    `']{background-color:rgba(255,221,0,0.45);}`
  ], activeMatchId);
});
const DummyRenderer = () => {
  const pageContext = usePageContext();
  if (!pageContext) throw new Error("Unable to find Page context.");
  const { page, scale, rotate } = pageContext;
  if (!page) throw new Error("Unable to find Page.");
  const viewport = useMemo(() => page.getViewport({
    scale,
    rotation: rotate
  }), [
    page,
    rotate,
    scale
  ]);
  return /* @__PURE__ */ jsx("div", {
    "data-dummy-renderer": true,
    style: {
      width: Math.floor(viewport.width),
      height: Math.floor(viewport.height),
      display: "block",
      userSelect: "none"
    }
  });
};
const RowPage = ({ pageNumber, width }) => {
  const [wrapperRef, setWrapperRef] = useState(null);
  const [renderedTextLayer, setRenderedTextLayer] = useState(0);
  const { getEstimatedHeight, activeMatchId, domModel } = usePDFContent();
  useEffect(() => {
    if (!domModel || !wrapperRef || renderedTextLayer === 0) return;
    domModel.parseDynamicChunk(wrapperRef, pageNumber - 1);
    return () => {
      domModel.unmountChunk(pageNumber - 1);
    };
  }, [
    domModel,
    pageNumber,
    wrapperRef,
    renderedTextLayer
  ]);
  const textLayerRendered = useRef(() => {
    setRenderedTextLayer((prev) => prev + 1);
  });
  const height = useMemo(() => getEstimatedHeight(pageNumber, width, true), [
    getEstimatedHeight,
    pageNumber,
    width
  ]);
  const pdfUiControls = usePDFViewControls();
  const debouncedZoomLevel = useDebounce(Math.min(pdfUiControls.zoomLevel, 3), 300);
  const isResizing = debouncedZoomLevel !== pdfUiControls.zoomLevel;
  const { isMobileView } = useResponsive();
  return /* @__PURE__ */ jsxs(Fragment, {
    children: [
      /* @__PURE__ */ jsx(PagePlaceholder, {
        pageNumber,
        absolute: true
      }),
      !isResizing && /* @__PURE__ */ jsx(Wrapper, {
        "data-row-page": true,
        ref: setWrapperRef,
        children: /* @__PURE__ */ jsx(Page, {
          /**
          * currently turned off, it's bricking the rendering performance
          */
          renderMode: isMobileView ? "custom" : "canvas",
          customRenderer: DummyRenderer,
          pageNumber,
          width,
          height,
          loading: /* @__PURE__ */ jsx(Fragment, {}),
          activeMatchId,
          onRenderTextLayerSuccess: textLayerRendered.current
        }, `${pageNumber}-page`)
      })
    ]
  });
};

export { RowPage as default };
//# sourceMappingURL=RowPage.js.map
